import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Search,
  RefreshCw,
  CheckCircle,
  Clock,
  AlertCircle,
  MessageSquare,
  Send,
  ExternalLink,
  Copy,
  Check,
  Calendar,
  Phone,
  User,
  Link2,
  X,
  Inbox,
  Lock,
  ChevronRight,
  ShieldAlert,
} from "lucide-react";
import clsx from "clsx";
import { Tooltip } from "../../ui/Tooltip.jsx";
import ticketsService from "../../../services/ticketsService";
import { useSignalRTickets } from "../../../hooks/useSignalRTickets";
import { formatearFechaWhatsApp, formatearFechaHora } from "../../../utils/ticketsHelpers";

export default function TicketsSinEvento({ initialTicketId = null }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tickets, setTickets] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros
  const [filtroEstado, setFiltroEstado] = useState("abierto"); // "abierto", "todos", "pendiente", "cerrado"
  const [searchTerm, setSearchTerm] = useState("");

  // Ticket seleccionado y sus datos
  const [ticketSeleccionado, setTicketSeleccionado] = useState(null);
  const [conversacion, setConversacion] = useState([]);
  const [loadingConversacion, setLoadingConversacion] = useState(false);
  const [nuevoMensaje, setNuevoMensaje] = useState("");
  const [enviandoMensaje, setEnviandoMensaje] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [cambiandoEstado, setCambiandoEstado] = useState(false);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Cargar tickets sin evento desde el API
  const cargarTickets = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError(null);
      const res = await ticketsService.getAllTickets({ sin_evento: true });
      if (res.success) {
        setTickets(res.tickets || []);
        if (res.estadisticas) {
          setEstadisticas(res.estadisticas);
        }
      } else {
        setError(res.error || "Error al cargar los tickets sin evento");
      }
    } catch (err) {
      console.error("Error al cargar tickets sin evento:", err);
      setError("No se pudieron cargar los tickets");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  // Cargar al montar
  useEffect(() => {
    cargarTickets();
  }, [cargarTickets]);

  // Cargar conversación cuando cambia el ticket seleccionado
  const cargarConversacion = useCallback(async (ticketId) => {
    if (!ticketId) return;
    try {
      setLoadingConversacion(true);
      const res = await ticketsService.getMessages(ticketId);
      if (res.success) {
        const msgs = res.mensajes || res.conversacion?.mensajes || [];
        setConversacion(msgs);
      } else {
        setConversacion([]);
      }
    } catch (err) {
      console.error("Error al cargar mensajes del ticket:", err);
      setConversacion([]);
    } finally {
      setLoadingConversacion(false);
    }
  }, []);

  // Seleccionar un ticket
  const handleSeleccionarTicket = useCallback(
    (ticket) => {
      setTicketSeleccionado(ticket);
      cargarConversacion(ticket.id);

      // Si tiene mensajes no leídos, marcar como leído en BD
      if (ticket.msg_no_leidos > 0 || ticket.leido === false) {
        ticketsService.marcarComoLeido(ticket.id).catch(() => {});
        setTickets((prev) =>
          prev.map((t) => (t.id === ticket.id ? { ...t, msg_no_leidos: 0, leido: true } : t))
        );
      }
    },
    [cargarConversacion]
  );

  // Cerrar la vista de detalle del ticket seleccionado
  const handleCerrarDetalle = useCallback(() => {
    setTicketSeleccionado(null);
    setConversacion([]);
    if (searchParams.get("ticketId")) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("ticketId");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Cambiar pestaña de filtro y cerrar detalle si hay alguno abierto
  const handleCambiarFiltroEstado = useCallback((nuevoEstado) => {
    setFiltroEstado(nuevoEstado);
    handleCerrarDetalle();
  }, [handleCerrarDetalle]);

  const ticketInicialProcesado = useRef(false);

  // Auto-seleccionar ticket de URL si viene por parámetro o initialTicketId
  useEffect(() => {
    const targetId = initialTicketId || searchParams.get("ticketId");
    if (targetId && tickets.length > 0 && !ticketInicialProcesado.current) {
      const encontrado = tickets.find((t) => t.id === targetId || t.ticket === targetId);
      if (encontrado) {
        handleSeleccionarTicket(encontrado);
        ticketInicialProcesado.current = true;
      }
    }
  }, [tickets, initialTicketId, searchParams, handleSeleccionarTicket]);

  // SignalR para actualizaciones en tiempo real
  useSignalRTickets({
    onNuevoTicket: (data) => {
      // Si el ticket nuevo es sin evento o se crea, refrescar lista
      if (!data?.evento_id || data?.nombre_evento === "N/A") {
        cargarTickets(true);
      }
    },
    onTicketActualizado: (data) => {
      cargarTickets(true);
      if (ticketSeleccionado && data?.id === ticketSeleccionado.id) {
        cargarConversacion(ticketSeleccionado.id);
      }
    },
    onNuevoMensaje: (data) => {
      if (ticketSeleccionado && (data?.ticketId === ticketSeleccionado.id || data?.conversacionID === ticketSeleccionado.id)) {
        cargarConversacion(ticketSeleccionado.id);
      } else {
        cargarTickets(true);
      }
    },
  });

  // Auto-scroll en chat cuando llegan mensajes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversacion]);

  // Filtrado de tickets por búsqueda y estado
  const ticketsFiltrados = useMemo(() => {
    return tickets.filter((ticket) => {
      // Filtro de estado
      if (filtroEstado === "abierto" && ticket.estatus !== "abierto") return false;
      if (filtroEstado === "pendiente" && ticket.estatus !== "pendiente") return false;
      if (filtroEstado === "cerrado" && ticket.estatus !== "cerrado") return false;

      // Filtro de búsqueda
      if (searchTerm.trim() !== "") {
        const query = searchTerm.toLowerCase();
        const nombre = (ticket.nombre || "").toLowerCase();
        const telefono = (ticket.telefono || "").toLowerCase();
        const idTicket = (ticket.ticket || ticket.id || "").toLowerCase();
        const mensaje = (ticket.mensaje || ticket.ultimo_mensaje || "").toLowerCase();

        return (
          nombre.includes(query) ||
          telefono.includes(query) ||
          idTicket.includes(query) ||
          mensaje.includes(query)
        );
      }

      return true;
    });
  }, [tickets, filtroEstado, searchTerm]);

  // Contadores por estado
  const conteos = useMemo(() => {
    return {
      todos: tickets.length,
      abiertos: tickets.filter((t) => t.estatus === "abierto").length,
      pendientes: tickets.filter((t) => t.estatus === "pendiente").length,
      cerrados: tickets.filter((t) => t.estatus === "cerrado").length,
    };
  }, [tickets]);

  // Copiar ID de ticket
  const handleCopiarTicket = (id) => {
    navigator.clipboard.writeText(id);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  // Cambiar estado de ticket
  const handleCambiarEstado = async (nuevoEstado) => {
    if (!ticketSeleccionado || cambiandoEstado) return;
    try {
      setCambiandoEstado(true);
      const res = await ticketsService.updateTicket(ticketSeleccionado.id, {
        estatus: nuevoEstado,
      });
      if (res.success) {
        setTicketSeleccionado((prev) => ({ ...prev, estatus: nuevoEstado }));
        setTickets((prev) =>
          prev.map((t) => (t.id === ticketSeleccionado.id ? { ...t, estatus: nuevoEstado } : t))
        );
      }
    } catch (err) {
      console.error("Error al cambiar estado:", err);
    } finally {
      setCambiandoEstado(false);
    }
  };

  // Enviar mensaje de chat
  const handleEnviarMensaje = async (e) => {
    e?.preventDefault();
    if (!nuevoMensaje.trim() || enviandoMensaje || !ticketSeleccionado) return;

    const texto = nuevoMensaje.trim();
    const tempMsg = {
      id: `temp-${Date.now()}`,
      from: "admin",
      remitente: "Asesor",
      texto: texto,
      timestamp: new Date().toISOString(),
      leido: true,
    };

    // Optimistic UI
    setConversacion((prev) => [...prev, tempMsg]);
    setNuevoMensaje("");
    setEnviandoMensaje(true);

    try {
      const res = await ticketsService.sendMessage({
        ticket: ticketSeleccionado.ticket || ticketSeleccionado.id,
        ticketId: ticketSeleccionado.id,
        telefono: ticketSeleccionado.telefono,
        mensaje: texto,
        texto: texto,
      });

      if (!res.success) {
        throw new Error(res.error || "No se pudo enviar el mensaje");
      }
    } catch (err) {
      console.error("Error al enviar mensaje:", err);
      // Recargar la conversación real en caso de error
      cargarConversacion(ticketSeleccionado.id);
    } finally {
      setEnviandoMensaje(false);
    }
  };

  return (
    <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden flex flex-col lg:flex-row h-[720px] transition-all">
      {/* ========================================================================= */}
      {/* PANEL IZQUIERDO: LISTA DE TICKETS SIN EVENTO                                */}
      {/* ========================================================================= */}
      <div className="w-full lg:w-[380px] shrink-0 border-r border-gray-100 dark:border-gray-800 flex flex-col bg-gray-50/50 dark:bg-[#151515]">
        {/* Cabecera y Buscador */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-800 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                Bandeja de Entrada
              </h2>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-casal/10 text-casal dark:bg-casal/20 dark:text-teal-300">
                {tickets.length}
              </span>
            </div>

            <Tooltip content="Recargar tickets" position="bottom">
              <button
                onClick={() => cargarTickets()}
                disabled={loading}
                className="p-1.5 rounded-lg text-gray-400 hover:text-casal hover:bg-gray-100 dark:hover:bg-gray-800 transition"
              >
                <RefreshCw size={16} className={clsx(loading && "animate-spin text-casal")} />
              </button>
            </Tooltip>
          </div>

          {/* Input Buscador */}
          <div className="relative">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Buscar por nombre, teléfono o ticket..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-8 py-2 text-xs bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-casal text-gray-800 dark:text-gray-100 placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filtros de Estado */}
          <div className="flex items-center gap-1 bg-gray-100/80 dark:bg-gray-800 p-1 rounded-xl text-xs font-medium">
            <button
              onClick={() => handleCambiarFiltroEstado("abierto")}
              className={clsx(
                "flex-1 py-1.5 px-2 rounded-lg transition text-center flex items-center justify-center gap-1",
                filtroEstado === "abierto"
                  ? "bg-white dark:bg-gray-700 text-casal dark:text-teal-300 font-bold shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              )}
            >
              <span>Abiertos</span>
              {conteos.abiertos > 0 && (
                <span className="w-4 h-4 text-[10px] rounded-full bg-red-500 text-white flex items-center justify-center">
                  {conteos.abiertos}
                </span>
              )}
            </button>

            <button
              onClick={() => handleCambiarFiltroEstado("pendiente")}
              className={clsx(
                "flex-1 py-1.5 px-2 rounded-lg transition text-center",
                filtroEstado === "pendiente"
                  ? "bg-white dark:bg-gray-700 text-amber-600 dark:text-amber-400 font-bold shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              )}
            >
              En espera ({conteos.pendientes})
            </button>

            <button
              onClick={() => handleCambiarFiltroEstado("cerrado")}
              className={clsx(
                "flex-1 py-1.5 px-2 rounded-lg transition text-center",
                filtroEstado === "cerrado"
                  ? "bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-bold shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              )}
            >
              Cerrados ({conteos.cerrados})
            </button>

            <button
              onClick={() => handleCambiarFiltroEstado("todos")}
              className={clsx(
                "flex-1 py-1.5 px-2 rounded-lg transition text-center",
                filtroEstado === "todos"
                  ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-bold shadow-sm"
                  : "text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              )}
            >
              Todos ({conteos.todos})
            </button>
          </div>
        </div>

        {/* Lista de Tickets */}
        <div className="flex-1 overflow-y-auto custom-scrollbar divide-y divide-gray-100 dark:divide-gray-800/60">
          {loading ? (
            <div className="p-8 text-center space-y-3">
              <RefreshCw className="animate-spin text-casal mx-auto" size={24} />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Buscando tickets sin evento...
              </p>
            </div>
          ) : ticketsFiltrados.length === 0 ? (
            <div className="p-10 text-center space-y-2">
              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto text-gray-400">
                <Inbox size={22} />
              </div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                No hay tickets en esta bandeja
              </p>
              <p className="text-xs text-gray-400 max-w-[200px] mx-auto">
                {searchTerm
                  ? "No se encontraron coincidencias para la búsqueda."
                  : "Todos los tickets sin evento han sido atendidos o archivados."}
              </p>
            </div>
          ) : (
            ticketsFiltrados.map((ticket) => {
              const isSelected = ticketSeleccionado?.id === ticket.id;
              const tieneNoLeidos = (ticket.msg_no_leidos || 0) > 0;
              const initial = (ticket.nombre || "U").charAt(0).toUpperCase();

              return (
                <div
                  key={ticket.id}
                  onClick={() => handleSeleccionarTicket(ticket)}
                  className={clsx(
                    "p-3.5 cursor-pointer transition flex items-start gap-3 relative group",
                    isSelected
                      ? "bg-casal/10 dark:bg-casal/20 border-l-4 border-casal"
                      : "hover:bg-white dark:hover:bg-gray-800/50"
                  )}
                >
                  {/* Avatar */}
                  <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-casal to-teal-700 text-white font-bold text-xs flex items-center justify-center shadow-sm">
                    {initial}
                  </div>

                  {/* Info Ticket */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-1 mb-0.5">
                      <h4
                        className={clsx(
                          "text-xs truncate",
                          tieneNoLeidos || isSelected
                            ? "font-bold text-gray-900 dark:text-white"
                            : "font-semibold text-gray-700 dark:text-gray-300"
                        )}
                      >
                        {ticket.nombre || "Contacto desconocido"}
                      </h4>
                      <span className="text-[10px] text-gray-400 shrink-0">
                        {formatearFechaWhatsApp(ticket.fecha_creacion)}
                      </span>
                    </div>

                    <p className="text-[11px] font-mono text-gray-400 dark:text-gray-500 mb-1">
                      {ticket.telefono}
                    </p>

                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 italic">
                      {ticket.ultimo_mensaje || ticket.mensaje || "Sin mensaje"}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      {/* Estado */}
                      <span
                        className={clsx(
                          "text-[10px] font-semibold px-2 py-0.5 rounded-full capitalize",
                          ticket.estatus === "abierto"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50"
                            : ticket.estatus === "pendiente"
                            ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-200/50"
                            : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                        )}
                      >
                        {ticket.estatus}
                      </span>

                      {/* Badge mensajes no leídos */}
                      {tieneNoLeidos && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center animate-pulse">
                          {ticket.msg_no_leidos}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* PANEL DERECHO: DETALLE DEL TICKET SELECCIONADO                             */}
      {/* ========================================================================= */}
      <div className="flex-1 flex flex-col bg-white dark:bg-[#1a1a1a] min-w-0">
        {!ticketSeleccionado ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center space-y-3">
            <div className="w-16 h-16 rounded-full bg-casal/10 dark:bg-casal/20 text-casal flex items-center justify-center">
              <MessageSquare size={28} />
            </div>
            <h3 className="text-base font-bold text-gray-800 dark:text-white">
              Selecciona un ticket sin evento
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 max-w-sm">
              Elige cualquiera de los tickets del panel izquierdo para revisar sus mensajes,
              responder directamente por WhatsApp o cambiar su estado.
            </p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col min-h-0">
            {/* Header del Ticket */}
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex flex-wrap items-center justify-between gap-3 bg-gray-50/30 dark:bg-gray-900/30">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-casal text-white font-bold text-sm flex items-center justify-center shadow-sm">
                  {(ticketSeleccionado.nombre || "U").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white">
                      {ticketSeleccionado.nombre || "Contacto sin nombre"}
                    </h3>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300/40">
                      Sin Evento Asignado
                    </span>
                  </div>

                  <div className="flex items-center gap-3 mt-0.5">
                    <button
                      onClick={() => handleCopiarTicket(ticketSeleccionado.ticket || ticketSeleccionado.id)}
                      className="text-xs font-mono text-gray-500 hover:text-casal flex items-center gap-1 group"
                    >
                      <span>{ticketSeleccionado.ticket || ticketSeleccionado.id}</span>
                      {copiado ? (
                        <Check size={12} className="text-emerald-500" />
                      ) : (
                        <Copy size={12} className="text-gray-400 group-hover:text-casal" />
                      )}
                    </button>

                    <a
                      href={`https://wa.me/52${ticketSeleccionado.telefono}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1 font-medium"
                    >
                      <Phone size={12} />
                      <span>{ticketSeleccionado.telefono}</span>
                      <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
              </div>

              {/* Acciones de Estado */}
              <div className="flex items-center gap-2">
                <select
                  value={ticketSeleccionado.estatus}
                  disabled={cambiandoEstado}
                  onChange={(e) => handleCambiarEstado(e.target.value)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-casal cursor-pointer"
                >
                  <option value="abierto">Abierto</option>
                  <option value="pendiente">En espera</option>
                  <option value="cerrado">Cerrado / Resuelto</option>
                </select>

                {ticketSeleccionado.estatus !== "cerrado" ? (
                  <button
                    onClick={() => handleCambiarEstado("cerrado")}
                    disabled={cambiandoEstado}
                    className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 transition flex items-center gap-1"
                  >
                    <CheckCircle size={14} className="text-emerald-500" />
                    <span>Cerrar Ticket</span>
                  </button>
                ) : (
                  <button
                    onClick={() => handleCambiarEstado("abierto")}
                    disabled={cambiandoEstado}
                    className="px-3 py-1.5 text-xs font-semibold rounded-xl bg-casal/10 hover:bg-casal/20 text-casal dark:text-teal-300 transition flex items-center gap-1"
                  >
                    <RefreshCw size={14} />
                    <span>Reabrir</span>
                  </button>
                )}

                <div className="h-5 w-px bg-gray-200 dark:bg-gray-700 mx-0.5" />

                <Tooltip content="Cerrar información" position="bottom">
                  <button
                    onClick={handleCerrarDetalle}
                    className="p-1.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition flex items-center justify-center shadow-sm"
                    title="Cerrar información"
                    aria-label="Cerrar información del ticket"
                  >
                    <X size={15} />
                  </button>
                </Tooltip>
              </div>
            </div>

            {/* Grid Interior: Info a la Izquierda, Chat a la Derecha */}
            <div className="flex-1 grid grid-cols-1 xl:grid-cols-12 overflow-hidden min-h-0">
              {/* ========================================================= */}
              {/* COLUMNA 1: INFO DE CONTACTO Y VINCULACIÓN (4 COLS)        */}
              {/* ========================================================= */}
              <div className="xl:col-span-4 border-r border-gray-100 dark:border-gray-800 p-4 overflow-y-auto custom-scrollbar space-y-4 bg-gray-50/30 dark:bg-black/10">
                {/* Tarjeta: Motivo de Solicitud */}
                <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 shadow-sm">
                  <div className="flex items-center gap-1.5 mb-2 text-casal dark:text-teal-400 font-bold text-xs">
                    <MessageSquare size={14} />
                    <span>Solicitud Inicial</span>
                  </div>
                  <p className="text-xs text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-900/60 p-3 rounded-xl border border-gray-100 dark:border-gray-800 leading-relaxed font-normal">
                    "{ticketSeleccionado.mensaje || ticketSeleccionado.ultimo_mensaje || "Sin mensaje especificado"}"
                  </p>
                </div>

                {/* Tarjeta: Datos de Contacto */}
                <div className="p-3.5 rounded-2xl bg-white dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700/60 shadow-sm space-y-2.5">
                  <div className="flex items-center gap-1.5 text-gray-800 dark:text-gray-200 font-bold text-xs mb-1">
                    <User size={14} className="text-casal" />
                    <span>Información de Contacto</span>
                  </div>

                  <div className="text-xs space-y-1.5">
                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                      <span className="text-gray-400">Nombre reportado:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-200">
                        {ticketSeleccionado.nombre || "No especificado"}
                      </span>
                    </div>

                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                      <span className="text-gray-400">Teléfono:</span>
                      <span className="font-mono text-gray-700 dark:text-gray-200">
                        {ticketSeleccionado.telefono}
                      </span>
                    </div>

                    <div className="flex justify-between py-1 border-b border-gray-100 dark:border-gray-800">
                      <span className="text-gray-400">Recibido:</span>
                      <span className="text-gray-700 dark:text-gray-200">
                        {formatearFechaHora(ticketSeleccionado.fecha_creacion)}
                      </span>
                    </div>

                    <div className="flex justify-between py-1">
                      <span className="text-gray-400">Canal:</span>
                      <span className="inline-flex items-center gap-1 font-medium text-emerald-600 dark:text-emerald-400">
                        <Phone size={11} /> WhatsApp Bot
                      </span>
                    </div>
                  </div>
                </div>

                {/* Tarjeta: Vincular a Evento (Opción a futuro) */}
                {/* <div className="p-3.5 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-gray-800/40 dark:to-gray-900/40 border border-dashed border-gray-300 dark:border-gray-700 shadow-sm space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-200 font-bold text-xs">
                      <Link2 size={14} className="text-casal" />
                      <span>Vincular a Evento</span>
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 flex items-center gap-1">
                      <Lock size={10} />
                      Próximamente
                    </span>
                  </div>

                  <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
                    Esta función permitirá vincular el número{" "}
                    <span className="font-mono font-semibold">{ticketSeleccionado.telefono}</span> a un
                    evento y asistente registrado en el sistema.
                  </p>

                  <div className="space-y-2 pt-1 opacity-60 pointer-events-none">
                    <select
                      disabled
                      className="w-full text-xs p-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-400 cursor-not-allowed"
                    >
                      <option>Seleccionar evento de destino...</option>
                    </select>
                    <button
                      disabled
                      className="w-full py-2 text-xs font-semibold rounded-xl bg-casal text-white opacity-50 cursor-not-allowed"
                    >
                      Vincular y Convertir en Ticket de Evento
                    </button>
                  </div>
                </div> */}
              </div>

              {/* ========================================================= */}
              {/* COLUMNA 2: CONVERSACIÓN / CHAT EN VIVO (8 COLS)            */}
              {/* ========================================================= */}
              <div className="xl:col-span-8 flex flex-col h-full bg-white dark:bg-[#1a1a1a] min-h-0">
                {/* Subheader Chat */}
                <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                      Chat de Soporte con el Usuario
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-400">
                    {conversacion.length} mensajes en total
                  </span>
                </div>

                {/* Lista de Mensajes */}
                <div
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto p-4 space-y-3 custom-scrollbar bg-slate-50/30 dark:bg-black/20"
                >
                  {loadingConversacion ? (
                    <div className="h-full flex flex-col items-center justify-center space-y-2">
                      <RefreshCw size={20} className="animate-spin text-casal" />
                      <span className="text-xs text-gray-400">Cargando conversación...</span>
                    </div>
                  ) : conversacion.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center space-y-2 text-center p-6">
                      <MessageSquare size={28} className="text-gray-300 dark:text-gray-600" />
                      <p className="text-xs text-gray-400">
                        No hay mensajes registrados aún en este ticket.
                      </p>
                    </div>
                  ) : (
                    conversacion.map((msg, idx) => {
                      const esUsuario = msg.from === "usuario";
                      const esSistema = msg.from === "sistema" || msg.remitente === "Sistema";
                      const esAdmin = msg.from === "admin" || msg.remitente === "Soporte" || msg.remitente === "Asesor";

                      if (esSistema) {
                        return (
                          <div key={msg.id || idx} className="flex justify-center my-2">
                            <span className="text-[11px] px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 text-center max-w-md">
                              {msg.texto}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div
                          key={msg.id || idx}
                          className={clsx(
                            "flex flex-col max-w-[80%] space-y-1",
                            esAdmin ? "ml-auto items-end" : "mr-auto items-start"
                          )}
                        >
                          <div className="flex items-center gap-1.5 text-[10px] text-gray-400 px-1">
                            <span className="font-semibold">
                              {esAdmin ? "Asesor / Soporte" : ticketSeleccionado.nombre || "Usuario"}
                            </span>
                            <span>•</span>
                            <span>{formatearFechaWhatsApp(msg.timestamp)}</span>
                          </div>

                          <div
                            className={clsx(
                              "p-3 rounded-2xl text-xs leading-relaxed break-words shadow-sm",
                              esAdmin
                                ? "bg-casal text-white rounded-br-none"
                                : "bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-none"
                            )}
                          >
                            {msg.texto}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input para responder */}
                <form
                  onSubmit={handleEnviarMensaje}
                  className="p-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-[#1a1a1a] flex items-center gap-2"
                >
                  <input
                    type="text"
                    placeholder="Escribe una respuesta para enviar por WhatsApp al usuario..."
                    value={nuevoMensaje}
                    disabled={enviandoMensaje || ticketSeleccionado.estatus === "cerrado"}
                    onChange={(e) => setNuevoMensaje(e.target.value)}
                    className="flex-1 py-2.5 px-4 text-xs bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-casal text-gray-800 dark:text-gray-100 placeholder-gray-400 disabled:opacity-50"
                  />
                  <button
                    type="submit"
                    disabled={
                      !nuevoMensaje.trim() ||
                      enviandoMensaje ||
                      ticketSeleccionado.estatus === "cerrado"
                    }
                    className="px-4 py-2.5 rounded-xl bg-casal hover:bg-casal/90 text-white font-semibold text-xs transition flex items-center gap-1.5 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send size={14} className={clsx(enviandoMensaje && "animate-spin")} />
                    <span>{enviandoMensaje ? "Enviando..." : "Enviar"}</span>
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
